/**
 * Tailored Canvases — customer preview/approval page.
 *
 * Renders one card per previewable canvas of an order and lets the customer
 * Approve or Request Changes on each. Works in two modes based on the URL:
 *   - multi  (order-level link):  ?order-id=<id>            → fetch all canvases
 *   - single (per-line-item link): ?order-id=<id>&lineItemId=<id>
 *
 * Config (app base URL + shop) comes from data-attributes on the root element;
 * nothing is hardcoded here.
 */
document.addEventListener('DOMContentLoaded', function () {
  var root = document.querySelector('[data-image-check]');
  if (!root) return;

  var API_BASE = (root.getAttribute('data-api-base') || '').replace(/\/+$/, '');
  var APPROVAL_ENDPOINT = '/api/order-line-item-approval';

  var params = new URLSearchParams(window.location.search);
  var orderId = params.get('order-id') || params.get('orderId');
  var lineItemId = params.get('lineItemId'); // null in multi mode
  // Priority: explicit ?shop= in the URL → dev override (section setting) →
  // the storefront's own domain. The override lets us test this theme on one
  // store while the app + orders live on another; empty in production.
  var shop =
    params.get('shop') ||
    root.getAttribute('data-shop-override') ||
    root.getAttribute('data-shop') ||
    '';
  // Per-order token from the preview link — required by the app endpoint.
  var token = params.get('token') || '';

  var loadingEl = root.querySelector('[data-ic-loading]');
  var emptyEl = root.querySelector('[data-ic-empty]');
  var errorEl = root.querySelector('[data-ic-error]');
  var allDoneEl = root.querySelector('[data-ic-all-done]');
  var cardsEl = root.querySelector('[data-ic-cards]');
  var orderNumberEl = root.querySelector('[data-ic-order-number]');
  var cardTemplate = root.querySelector('[data-ic-card-template]');

  // Shared decline modal
  var modalEl = root.querySelector('[data-ic-modal]');
  var reasonEl = modalEl.querySelector('[data-ic-reason]');
  var counterEl = modalEl.querySelector('[data-ic-counter]');
  var confirmRejectEl = modalEl.querySelector('[data-ic-confirm-reject]');
  var modalSubtitleEl = modalEl.querySelector('[data-ic-modal-subtitle]');
  var activeReject = null; // { item, card }

  var TERMINAL = { approved: true, rejected: true };

  function show(el) {
    if (el) el.hidden = false;
  }
  function hide(el) {
    if (el) el.hidden = true;
  }

  function apiUrl(extraQuery) {
    var q = '?order-id=' + encodeURIComponent(orderId);
    if (shop) q += '&shop=' + encodeURIComponent(shop);
    if (token) q += '&token=' + encodeURIComponent(token);
    if (extraQuery) q += extraQuery;
    return API_BASE + APPROVAL_ENDPOINT + q;
  }

  function post(payload) {
    var body = token
      ? Object.assign({}, payload, { token: token })
      : payload;
    return fetch(API_BASE + APPROVAL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (data) {
        return { ok: res.ok, data: data };
      });
    });
  }

  // ---- rendering ---------------------------------------------------------

  function renderPersonalization(listEl, personalization) {
    listEl.innerHTML = '';
    (personalization || []).forEach(function (p) {
      if (!p || !p.key) return;
      var li = document.createElement('li');
      li.textContent = p.key + ': ' + (p.value || '');
      listEl.appendChild(li);
    });
  }

  function setCardState(card, status) {
    var statusEl = card.querySelector('[data-ic-status]');
    var approveBtn = card.querySelector('[data-ic-approve]');
    var requestBtn = card.querySelector('[data-ic-request]');
    var terminal = !!TERMINAL[status];

    card.setAttribute('data-status', status);

    if (status === 'approved') {
      statusEl.textContent = 'Approved';
      statusEl.className = 'ic-card__status ic-card__status--approved';
      show(statusEl);
    } else if (status === 'rejected') {
      statusEl.textContent = 'Changes requested — our team will be in touch.';
      statusEl.className = 'ic-card__status ic-card__status--rejected';
      show(statusEl);
    } else {
      hide(statusEl);
    }

    approveBtn.disabled = terminal;
    requestBtn.disabled = terminal;
  }

  function fillCard(item) {
    var frag = cardTemplate.content.cloneNode(true);
    var card = frag.querySelector('[data-ic-card]');

    // Image
    var imageWrap = card.querySelector('[data-ic-image]');
    if (item.imageUrl) {
      var img = document.createElement('img');
      img.src = item.imageUrl;
      img.alt = item.productName || 'Canvas preview';
      img.loading = 'lazy';
      imageWrap.appendChild(img);
    }

    // Details
    card.querySelector('[data-ic-product]').textContent = item.productName || '';
    var qtyEl = card.querySelector('[data-ic-quantity]');
    if (item.quantity != null) {
      qtyEl.innerHTML = '<strong>Qty:</strong> ' + item.quantity;
    }
    renderPersonalization(
      card.querySelector('[data-ic-personalization]'),
      item.personalization
    );

    // Actions
    var approveBtn = card.querySelector('[data-ic-approve]');
    var requestBtn = card.querySelector('[data-ic-request]');

    approveBtn.addEventListener('click', function () {
      handleApprove(item, card, approveBtn, requestBtn);
    });
    requestBtn.addEventListener('click', function () {
      openRejectModal(item, card);
    });

    setCardState(card, item.lineItemStatus || 'pending');
    cardsEl.appendChild(card);
  }

  function renderCards(items) {
    cardsEl.innerHTML = '';
    items.forEach(fillCard);
    updateAllDone();
  }

  function updateAllDone() {
    var cards = cardsEl.querySelectorAll('[data-ic-card]');
    if (!cards.length) return;
    var allTerminal = Array.prototype.every.call(cards, function (c) {
      return TERMINAL[c.getAttribute('data-status')];
    });
    if (allTerminal) show(allDoneEl);
    else hide(allDoneEl);
  }

  // ---- actions -----------------------------------------------------------

  function handleApprove(item, card, approveBtn, requestBtn) {
    approveBtn.disabled = true;
    requestBtn.disabled = true;
    post({
      intent: 'manually_approve_line_item',
      orderId: orderId,
      lineItemId: item.lineItemId,
      shop: shop || undefined,
    })
      .then(function (r) {
        if (!r.ok || (r.data && r.data.error)) {
          throw new Error((r.data && r.data.error) || 'Approve failed');
        }
        setCardState(card, 'approved');
        updateAllDone();
      })
      .catch(function () {
        // re-enable so the customer can retry
        setCardState(card, item.lineItemStatus || 'pending');
        window.alert('Sorry, we could not approve this canvas. Please try again.');
      });
  }

  function openRejectModal(item, card) {
    activeReject = { item: item, card: card };
    modalSubtitleEl.textContent = item.productName
      ? 'For: ' + item.productName
      : '';
    reasonEl.value = '';
    counterEl.textContent = '0/500';
    confirmRejectEl.disabled = true;
    modalEl.classList.add('is-open');
    reasonEl.focus();
  }

  function closeRejectModal() {
    modalEl.classList.remove('is-open');
    activeReject = null;
  }

  function confirmReject() {
    if (!activeReject) return;
    var reason = reasonEl.value.trim();
    if (!reason) return;
    var item = activeReject.item;
    var card = activeReject.card;

    confirmRejectEl.disabled = true;
    post({
      intent: 'manually_reject',
      orderId: orderId,
      lineItemId: item.lineItemId,
      reason: reason,
      productName: item.productName || undefined,
      shop: shop || undefined,
    })
      .then(function (r) {
        if (!r.ok || (r.data && r.data.error)) {
          throw new Error((r.data && r.data.error) || 'Request failed');
        }
        setCardState(card, 'rejected');
        closeRejectModal();
        updateAllDone();
      })
      .catch(function () {
        confirmRejectEl.disabled = false;
        window.alert('Sorry, we could not submit your request. Please try again.');
      });
  }

  // ---- modal wiring ------------------------------------------------------

  modalEl.querySelectorAll('[data-ic-modal-close]').forEach(function (btn) {
    btn.addEventListener('click', closeRejectModal);
  });
  modalEl.addEventListener('click', function (e) {
    if (e.target === modalEl) closeRejectModal();
  });
  reasonEl.addEventListener('input', function () {
    var len = reasonEl.value.length;
    counterEl.textContent = len + '/500';
    confirmRejectEl.disabled = reasonEl.value.trim().length === 0;
  });
  confirmRejectEl.addEventListener('click', confirmReject);

  // ---- fetch + boot ------------------------------------------------------

  function fetchData() {
    var url = lineItemId
      ? apiUrl('&lineItemId=' + encodeURIComponent(lineItemId))
      : apiUrl();

    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('Request failed: ' + res.status);
      return res.json();
    });
  }

  function boot() {
    if (!API_BASE || !orderId) {
      hide(loadingEl);
      show(errorEl);
      return;
    }

    fetchData()
      .then(function (data) {
        hide(loadingEl);
        if (data && data.error) {
          show(errorEl);
          return;
        }

        // Normalise single vs multi into an items array.
        var items;
        var orderNumber;
        if (lineItemId) {
          orderNumber = data.orderNumber;
          items = [
            {
              lineItemId: lineItemId,
              imageUrl: data.imageUrl,
              productName: data.productName,
              sku: data.sku,
              quantity: data.quantity,
              personalization: data.personalization,
              lineItemStatus: data.lineItemStatus,
            },
          ];
        } else {
          orderNumber = data.orderNumber;
          items = data.items || [];
        }

        if (orderNumber && orderNumberEl) {
          orderNumberEl.textContent = 'Order ' + orderNumber;
          orderNumberEl.hidden = false;
        }

        if (!items.length) {
          show(emptyEl);
          return;
        }

        renderCards(items);
      })
      .catch(function () {
        hide(loadingEl);
        show(errorEl);
      });
  }

  boot();
});
